/**
 * How an image watermark stops its own white from washing out the page
 * (VE-28770).
 *
 * A logo is a rectangle and the artwork inside it is not, so its corners are
 * white — and that white, composited at any real opacity, fades every word it
 * covers. Two ways to deal with it, and they fail in opposite directions:
 *
 *   'multiply'  A PDF blend mode (`/BM /Multiply`) plus the matching CSS
 *               `mix-blend-mode` on the preview. White is multiply's identity,
 *               so paper leaves the page exactly as it found it. There is no
 *               algorithm here and nothing to get wrong on unusual artwork: it
 *               cannot erase a logo, cannot misjudge a threshold, and behaves
 *               the same on every image. Its cost is visible and predictable —
 *               black is multiply's absorbing element, so a mark crossing a
 *               black redaction box disappears into it, and a pale mark fades
 *               toward nothing on a pale page.
 *
 *   'knockout'  Remove the white from the raster instead (`knockOutBackground`)
 *               and composite normally. Keeps a mark visible over a redaction
 *               box, keeps a pale logo pale, and handles off-white paper the
 *               blend cannot. Its cost is that it is an image-processing
 *               algorithm — a threshold, a border-seeded flood fill and an edge
 *               feather — and each of those has artwork it reads wrongly, in
 *               ways that are silent rather than visible. See the follow-up
 *               ticket for the catalogue.
 *
 * MULTIPLY IS THE DEFAULT because of how the two fail, not because of what they
 * achieve when they work. A blend that makes a mark invisible over a black box
 * is something an operator can see in the preview and judge. A key that quietly
 * erases three-quarters of a logo, or drops a 1px stroke to 19% alpha, looks
 * healthy everywhere until the exported file reaches whoever asked for it.
 *
 * The knockout is kept rather than deleted so the comparison stays available.
 *
 * Flipping this constant switches the three surfaces that DRAW A MARK ON A
 * DOCUMENT together — the burn-in, the editor overlay and the derive that feeds
 * both — which is the point of one constant rather than a check in each.
 *
 * It does NOT reach the settings drawer's own previews, which hardcode the
 * blend and render the raw artwork without going through `deriveWatermarkImage`
 * at all. That predates this constant and agrees with it while the default is
 * `'multiply'`; under `'knockout'` the drawer would preview a mark differently
 * from the editor and the export. Wiring it up belongs with whichever mode wins
 * — see the follow-up ticket — not with keeping both available.
 */
export type WatermarkWhiteRemoval = 'multiply' | 'knockout';

export const WATERMARK_WHITE_REMOVAL: WatermarkWhiteRemoval = 'multiply';
